import Checkbox, { CheckboxProps } from '@storeblocks/checkbox-atom';
import Checkboxes from '@storeblocks/checkboxes';
import { type FC } from 'react';
import styled from 'styled-components';

import { usePortfoliosQuery } from '@/api/portfolios';
import { Divider } from '@/components/Divider';
import { useTexts } from '@/hooks/useTexts';

import { usePresentationContext } from './PresentationContext';

export const PortfolioGroupCheckboxes: FC = () => {
  const texts = useTexts();

  const { data } = usePortfoliosQuery();

  const { globalFilter, setGlobalFilter } = usePresentationContext();

  const options = data!.portfolioGroups.map<CheckboxProps>(
    (portfolioGroup) => ({
      id: portfolioGroup.id,
      value: portfolioGroup.id,
      label: `${portfolioGroup.id} - ${portfolioGroup.name}`,
      checked: globalFilter.portfolioGroups.some((portfolio) => {
        return portfolio.id === portfolioGroup.id;
      }),
    }),
  );

  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    if (event.target.checked) {
      // If all portfolio groups are selected, we can reset the global filter to include all.
      setGlobalFilter((prevFilter) => ({
        ...prevFilter,
        portfolioGroups: data!.portfolioGroups,
      }));
    } else {
      // If not all portfolio groups are selected, we can reset the global filter to an empty array.
      setGlobalFilter((prevFilter) => ({
        ...prevFilter,
        portfolioGroups: [],
      }));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setGlobalFilter((prevFilter) => {
      if (event.target.checked) {
        const selectedPortfolioGroup = data!.portfolioGroups.find(
          (portfolioGroup) => portfolioGroup.id === event.target.value,
        )!;

        return {
          ...prevFilter,
          portfolioGroups: [
            ...prevFilter.portfolioGroups,
            selectedPortfolioGroup,
          ],
        };
      }

      return {
        ...prevFilter,
        portfolioGroups: prevFilter.portfolioGroups.filter(
          (portfolioGroup) => portfolioGroup.id !== event.target.value,
        ),
      };
    });
  };

  return (
    <>
      <StyledCheckbox
        id="select-all-portfolio-groups"
        name="select-all-portfolio-groups"
        onChange={handleSelectAllChange}
        value="select-all-portfolio-groups"
        label={texts.pages.presentation.selectAllPortfolioGroups}
        checked={
          data!.portfolioGroups.length === globalFilter.portfolioGroups.length
        }
      />
      <Divider margin="8" />
      <Checkboxes
        groupName="choose-portfolio-groups"
        onChange={handleChange}
        options={options}
      />
    </>
  );
};

const StyledCheckbox = styled(Checkbox)`
  font-weight: 600;
`;
